import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, Share2, ChevronRight, ExternalLink, BookOpen, Layers, Star, CheckCircle } from 'lucide-react';

// Import Shared Components
import { AISnippetBlock, formatQuickAnswer, generateKeyPoints } from '@/components/AISnippetBlock';
import { PeopleAlsoAsk, DEFAULT_IPTV_FAQ } from '@/components/PeopleAlsoAsk';
import { BreadcrumbNav, createBlogBreadcrumbs } from '@/components/BreadcrumbNav';
import { SchemaGenerator } from '@/components/SchemaGenerator';
import { DEEP_LINKING_MAP, getRelatedArticles } from '@/components/BlogArticleContent';
import { getArticleImages, getOptimizedSearchQuery } from '@/services/imageService';
import { getCurrentMonthYear } from '@/services/eliteSummaryGenerator';
import { TableOfContents } from '@/components/TableOfContents';
import { PillarContentRenderer } from '@/components/PillarContentRenderer';
import { loadAllArticles } from '@/services/articleLoader';
import { ArticleTags } from '@/components/ArticleTags';
import { Footer } from '@/components/Footer';
import { ArticleFooter } from '@/components/ArticleFooter';
import '@/styles/eliteDesignSystem.css';

/**
 * PillarLayout - The "Ultimate Guide" Experience
 * 
 * Optimized for:
 * 1. Authority & Trust (High-end design)
 * 2. Long-form Readability (Chapter-based navigation)
 * 3. Topic Clustering (Visualizing related content)
 * 4. Conversion (Strategic placement of CTAs)
 */
export const PillarLayout = ({ article }) => {
    if (!article) return null;

    // State
    const [featuredImage, setFeaturedImage] = useState(null);
    const [imageLoading, setImageLoading] = useState(true);
    const [allArticles, setAllArticles] = useState([]);

    // Load Data
    useEffect(() => {
        const loadData = async () => {
            try {
                const articles = await loadAllArticles();
                if (articles && articles.length > 0) setAllArticles(articles);
            } catch (e) {
                console.warn('Error loading articles', e);
            }
        };
        loadData();
    }, []);

    // Fetch Image
    useEffect(() => {
        const fetchImage = async () => {
            try {
                setImageLoading(true);
                const searchQuery = getOptimizedSearchQuery(article.title + " ultimate guide");
                const images = await getArticleImages(searchQuery, 1);
                if (images.length > 0) setFeaturedImage(images[0]);
            } catch (e) {
                console.error(e);
            } finally {
                setImageLoading(false);
            }
        };
        fetchImage();
    }, [article.title, article.id]);

    // Derived Data
    const quickAnswer = article.quickAnswer || formatQuickAnswer(article);
    const keyPoints = article.keyPoints || generateKeyPoints(article);
    const faqItems = article.faqItems || DEFAULT_IPTV_FAQ;
    const lastUpdated = getCurrentMonthYear();
    const breadcrumbs = createBlogBreadcrumbs(article);

    // Find related content for cluster map
    const deepLinkedRelated = allArticles.length > 0 && getRelatedArticles
        ? getRelatedArticles(article.slug, allArticles)
        : [];

    // Pillar Header Style
    return (
        <>
            {/* TableOfContents removed - new design doesn't use floating sidebar */}

            <SchemaGenerator
                article={{
                    ...article,
                    datePublished: article.date,
                    dateModified: `2026-01-${String(Math.min(article.id + 10, 31)).padStart(2, '0')}`,
                    keywords: `${article.category}, ultimate guide, iptv 2026, ${article.title.toLowerCase()}`
                }}
                faqItems={faqItems}
                breadcrumbs={breadcrumbs}
                type="TechArticle" // More specific for technical guides
            />

            <div className="min-h-screen bg-background">
                {/* HERO SECTION: Distinct Dark Mode "Ultimate Guide" Header */}
                <section className="relative pt-24 pb-16 px-4 overflow-hidden">
                    {/* Background Glow */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl">
                        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[120px]" />
                        <div className="absolute top-10 right-1/4 w-[300px] h-[300px] bg-blue-500/10 rounded-full blur-[100px]" />
                    </div>

                    <div className="relative max-w-4xl mx-auto text-center z-10">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-6 backdrop-blur-sm">
                                <BookOpen className="w-4 h-4 text-primary" />
                                <span className="text-sm font-semibold text-primary tracking-wide uppercase">
                                    Ultimate {article.category} Guide 2026
                                </span>
                            </div>

                            <h1 className="text-4xl md:text-6xl font-extrabold mb-6 tracking-tight leading-tight bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-white/70">
                                {article.title}
                            </h1>

                            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
                                {article.excerpt}
                            </p>

                            <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground">
                                <div className="flex items-center gap-2">
                                    <div className="p-1.5 bg-green-500/10 rounded-full">
                                        <CheckCircle className="w-4 h-4 text-green-500" />
                                    </div>
                                    <span>Updated {lastUpdated}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="p-1.5 bg-blue-500/10 rounded-full">
                                        <Clock className="w-4 h-4 text-blue-500" />
                                    </div>
                                    <span>{article.readTime} Read</span>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* MAIN CONTENT CONTAINER */}
                <div className="max-w-7xl mx-auto px-4 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12">

                    {/* LEFT CONTENT COLUMN */}
                    <main className="lg:col-span-8 lg:col-start-3">
                        {/* 🔍 ELITE SUMMARY BLOCK */}
                        <div className="mb-12">
                            <AISnippetBlock
                                quickAnswer={quickAnswer}
                                keyPoints={keyPoints}
                                lastUpdated={lastUpdated}
                                variant="featured"
                                content={article.content}
                            />
                        </div>

                        {/* FEATURED IMAGE */}
                        {featuredImage && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="mb-12 rounded-2xl overflow-hidden border border-white/10 shadow-2xl"
                            >
                                <img
                                    src={featuredImage.url}
                                    alt={article.title}
                                    className="w-full aspect-[21/9] object-cover"
                                />
                            </motion.div>
                        )}

                        {/* CONTENT BODY */}
                        <div className="prose prose-invert prose-lg max-w-none">
                            <PillarContentRenderer content={article.content} />
                        </div>

                        {/* PILLAR CLUSTER MAP */}
                        {DEEP_LINKING_MAP[article.slug] && (
                            <div className="my-16 p-8 bg-card/30 border border-white/10 rounded-2xl relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-32 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />

                                <div className="relative z-10">
                                    <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                                        <Layers className="w-6 h-6 text-primary" />
                                        Master This Topic
                                    </h3>
                                    <p className="text-muted-foreground mb-8">
                                        This guide is part of our comprehensive {article.category} series. Explore these related deep-dives to become an expert:
                                    </p>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {DEEP_LINKING_MAP[article.slug].map((link, idx) => {
                                            // Only show if article exists in known list
                                            const linkExists = allArticles.find(a => a.slug === link.slug);
                                            if (!linkExists) return null;

                                            return (
                                                <Link
                                                    key={idx}
                                                    to={`/blog/${link.slug}`}
                                                    className="flex items-start gap-4 p-4 bg-background/50 border border-white/5 rounded-xl hover:border-primary/50 transition group"
                                                >
                                                    <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-primary/10 rounded-lg text-primary font-bold text-sm group-hover:bg-primary group-hover:text-white transition">
                                                        {idx + 1}
                                                    </span>
                                                    <div>
                                                        <h4 className="font-semibold text-sm mb-1 group-hover:text-primary transition">
                                                            {link.anchor}
                                                        </h4>
                                                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                                                            Read Guide <ChevronRight className="w-3 h-3" />
                                                        </span>
                                                    </div>
                                                </Link>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* PEOPLE ALSO ASK */}
                        <div className="mt-16">
                            <PeopleAlsoAsk faqItems={faqItems} title="Expert Q&A" variant="default" />
                        </div>

                        {/* ARTICLE TAGS */}
                        {article.tags && article.tags.length > 0 && (
                            <div className="mt-12 pt-8 border-t border-white/10">
                                <ArticleTags tags={article.tags} variant="featured" />
                            </div>
                        )}

                        {/* Article Footer - Share & Navigation */}
                        <ArticleFooter
                            title={article.title}
                            nextArticleTitle={deepLinkedRelated[0]?.title || 'Best IPTV USA 2026'}
                            nextArticleUrl={deepLinkedRelated[0] ? `/blog/${deepLinkedRelated[0].slug}` : '/blog/best-iptv-usa-2026'}
                        />

                    </main>
                </div>
            </div>
            {/* Footer moved to BlogArticlePage */}
        </>
    );
};
